import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Borrower } from '../../../models/borrower-model';
import { ResidencyBasis } from '../../../models/enums/residency-basis.enum';
import { ResidencyType } from '../../../models/enums/residency-type.enum';
import { ResidencyAddress } from '../../../models/residency-address.model';
import { ContextInjector } from '../../../models/wizard/config/context-injector';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import { OwnRentStep } from '../../../models/wizard/own-rent-step-model';
import { OwnRentStepConfig } from '../../../models/wizard/config/own-rent-step-config.model';
import { LocalizedEditService } from '../../../transloco/localized-edit.service';
import { isPresentAddressTheRefiProperty } from 'projects/borrower-app/src/app/wizard/refi-current-address.util';

@Component({
    selector: 'own-rent',
    templateUrl: 'own-rent.component.html',
    standalone: false
})
export class OwnRentComponent
  extends WizardStepComponentBase<OwnRentStep, OwnRentStepConfig>
  implements OnInit, OnDestroy {

  private _currentBorrower: Borrower;
  public currentAddress!: ResidencyAddress;

  private _langChangeSub?: Subscription;

  public get isOwned(): boolean | undefined {
    return this.currentAddress?.residencyBasis == ResidencyBasis.Own;
  }

  public get isOwnOrRentSelected(): boolean {
    //what to do if current borrower is null?
    return this.currentAddress.residencyBasis !== undefined;
  }

  public get isRent(): boolean {
    return this.currentAddress?.residencyBasis == ResidencyBasis.Rent;
  }

  public get isRentFree(): boolean {
    return this.currentAddress?.residencyBasis == ResidencyBasis.LivingRentFree;
  }

  onOwnedSelected = () => {
    this.currentAddress.residencyBasis = ResidencyBasis.Own;
    this.wizardFlowService.navigateForward();
  };

  onRentSelected = () => {
    this.currentAddress.residencyBasis = ResidencyBasis.Rent;
    this.wizardFlowService.navigateForward();
  };

  onRentFreeSelected = () => {
    this.currentAddress.residencyBasis = ResidencyBasis.LivingRentFree;
    this.wizardFlowService.navigateForward();
  };

  constructor(
    private readonly _injector: Injector,
    public readonly i18n: LocalizedEditService,
  ) {
    super(_injector);

    this._currentBorrower = this.wizardFlowService.context.currentBorrower;
    let currentAddress = this._currentBorrower.residencyAddresses.find(
      (a) => a.residencyType == ResidencyType.PresentAddress
    );
    if (!currentAddress) {
      currentAddress = new ResidencyAddress(ResidencyType.PresentAddress);
      this._currentBorrower.residencyAddresses.push(currentAddress);
    }
    this.currentAddress = currentAddress;
    if (this.mortgageApplication.subjectProperty.purposeOfLoan === 'Refinance') {
      if (isPresentAddressTheRefiProperty(this.currentAddress, this.mortgageApplication.subjectProperty)) {
        this.currentAddress.residencyBasis = ResidencyBasis.Own;
        if (this.cameHereWithBackNavigation) {
          this.wizardFlowService.navigateBackward();
        } else {
          this.onNextClicked();
        }
        return;
      }
    }
  }

  ngOnInit() {
    this.step.requiresNextButton = (this.isOwned || this.isRent || this.isRentFree);
    this.updateTitleForLang();
    this._langChangeSub = this.translocoService.langChanges$.subscribe(() => {
      this.updateTitleForLang();
    });
  }

  ngOnDestroy(): void {
    this._langChangeSub?.unsubscribe();
  }

  private updateTitleForLang = (): void => {
    this.step.title = this.translocoService.translate('wizard.ownRent.regardingAddress', {
      address: this.currentAddress.address?.address1 ?? '',
    });
    // Force the step template to recompute contextBoundStepProperties since
    // our title update may race with its own langChanges$ listener.
    if (this.template) {
      this.template.contextBoundStepProperties = ContextInjector.injectContextToStep(
        this.wizardFlowService.context,
        this.step,
        this.translocoService.getActiveLang(),
        (key, lang) => (this.translocoService.getTranslation(lang) as Record<string, string>)?.[key],
        this.translocoService.getDefaultLang(),
      );
    }
  };
}
